import React from 'react';
import { injectIntl } from '../../util/reactIntl';
import CustomOfferModal from './CustomOfferModal';
import { types as sdkTypes } from '../../util/sdkLoader';

const { UUID } = sdkTypes;

// The modal needs the intl object as a prop, so wrap it for the styleguide.
const OpenCustomOfferModal = injectIntl(props => (
  <CustomOfferModal isOpen onClose={() => null} onManageDisableScrolling={() => null} {...props} />
));

const offer = {
  amount: 250000,
  title: 'Verkkosivujen uudistus ja SEO-optimointi',
  description:
    'Suunnittelen ja toteutan uuden verkkosivuston WordPressillä. Sisältää etusivun, palvelusivut ja yhteydenottolomakkeen sekä perus-SEO:n.',
  startDate: '2026-10-01',
  deliveryDate: '2026-11-15',
};

// Offer made before the start date field existed: the delivery date stands alone.
export const CustomerWithoutStartDate = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: { ...offer, startDate: undefined },
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
  },
  group: 'transaction',
};

// Customer looking at a pending offer that has a title.
export const CustomerPending = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: offer,
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
  },
  group: 'transaction',
};

// Customer looking at a pending offer with the invoice payment option offered.
export const CustomerPendingInvoiceRequest = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: offer,
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
    invoiceOption: 'request',
    paymentTermDays: 14,
    onRequestInvoicing: () => null,
    acceptOfferInProgress: false,
  },
  group: 'transaction',
};

// Approved invoice customer: both payment methods can be chosen, so the note
// under them says why the VAT and the total follow the method.
export const CustomerPendingInvoiceAvailable = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: { ...offer, processName: 'flex-default-process' },
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
    invoiceOption: 'available',
    paymentTermDays: 14,
    vatPreview: {
      status: 'ok',
      resolution: {
        path: 'invoice',
        supplies: {
          core: { case: 'CS-1', rate: 0.255, label: 'VAT', noteKey: 'STANDARD' },
        },
      },
      reason: null,
      path: 'invoice',
    },
    onRetryVatPreview: () => null,
  },
  group: 'transaction',
};

// Customer whose VAT number VIES has not answered for yet: the hold notice
// with its spinner, the secondary retry, and the disabled accept with its hint.
export const CustomerPendingVatHold = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: { ...offer, processName: 'flex-default-process' },
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
    invoiceOption: 'request',
    vatPreview: {
      status: 'hold',
      resolution: null,
      reason: 'VAT_VALIDATION_PENDING',
      path: 'stripe',
    },
    onRetryVatPreview: () => null,
  },
  group: 'transaction',
};

// Customer who just saved billing details: the preview is being fetched, so
// the check shows in place of the form they submitted.
export const CustomerPendingBillingChecking = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: { ...offer, processName: 'flex-default-process' },
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
    invoiceOption: 'request',
    vatPreview: { status: 'needs-billing', resolution: null, reason: null, path: 'stripe' },
    vatPreviewInProgress: true,
    onRetryVatPreview: () => null,
  },
  group: 'transaction',
};

// Provider looking at an offer made before titles existed.
export const ProviderWithoutTitle = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: { ...offer, title: undefined },
    isCustomer: false,
    transactionLastTransition: 'transition/make-custom-offer',
    onUpdateOffer: () => null,
  },
  group: 'transaction',
};

const attachment = (id, name, size, state) => ({
  id: new UUID(`fa-${id}`),
  type: 'fileAttachment',
  attributes: { scope: 'protected', deleted: false },
  file: {
    id: new UUID(`f-${id}`),
    type: 'file',
    attributes: { name, size, state, deleted: false },
  },
});

// Customer looking at a pending offer that carries files, one still scanning.
export const CustomerPendingWithAttachments = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: {
      ...offer,
      attachments: [
        attachment('a', 'tarjous.pdf', 245760, 'available'),
        attachment('b', 'luonnos.png', 1048576, 'pendingVerification'),
      ],
    },
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
  },
  group: 'transaction',
};

// Approved invoice customer: "Jatka" opens the confirm step with the panel.
export const CustomerInvoiceConfirm = {
  component: OpenCustomOfferModal,
  props: {
    customOfferData: { ...offer, lineItems: [] },
    isCustomer: true,
    transactionLastTransition: 'transition/make-custom-offer',
    onAcceptCustomOffer: () => null,
    invoiceOption: 'available',
    paymentTermDays: 14,
    vatPreview: {
      status: 'ok',
      reason: null,
      path: 'invoice',
      buyerValidation: 'VERIFIED',
      resolution: {
        path: 'invoice',
        supplies: {
          customerSale: { case: 'CS-3', rate: 0.255, label: 'VAT', noteKey: 'STANDARD' },
          providerSale: { case: 'CS-3', rate: 0.255, label: 'VAT', noteKey: 'STANDARD' },
          serviceFee: { case: 'CS-3', rate: 0.255, label: 'VAT', noteKey: 'STANDARD' },
        },
      },
    },
    billingInitialValues: {
      customerType: 'BUSINESS',
      legalName: 'Testi Oy',
      country: 'FI',
      postalCode: '00100',
      street: 'Testikatu 1',
      city: 'Helsinki',
      vatNumber: 'FI31503663',
      businessProofRef: '',
    },
    onSaveBillingDetails: () => Promise.resolve(),
  },
  group: 'transaction',
};
